import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Loader, ArrowUpCircle } from 'lucide-react';
import OptionButton from './OptionButton';
import { AI_OPTIONS } from '../constants';
import { useSmartPosition } from '../hooks/useSmartPosition';

/**
 * TextMenu Component
 * - Floating menu that appears on text selection
 * - Contains query input and AI text transformation options
 * - Intelligently positioned to avoid viewport overflow
 */
const TextMenu = ({
  isLoading,
  onAskAI,
  onOptionSelect,
  selectedText,
  menuRef,
  referenceElement
}) => {
  const [askQuery, setAskQuery] = useState('');
  const [isMounted, setIsMounted] = useState(false);

  // Track when component is mounted and rendered
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Use smart positioning hook only after component is mounted
  const position = useSmartPosition(
    referenceElement,
    menuRef.current,
    isMounted
  );

  const handleAskAI = () => {
    if (!askQuery || askQuery.trim() === '') return;
    onAskAI(askQuery);
    setAskQuery('');
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleAskAI();
    }
  };

  const menuContent = (
    <div
      ref={menuRef}
      className={`ai-menu ai-menu-smart ${
        position.placement === 'top' ? 'ai-menu-above' : 'ai-menu-below'
      }`}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 1120
      }}
    >
      <div className="ai-ask-container">
        <input
          type="text"
          className="ai-ask-input"
          placeholder="Preguntar a la IA..."
          value={askQuery}
          onChange={e => setAskQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <button
          className="ai-ask-button"
          type="button"
          onClick={handleAskAI}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader className="rotate" size={16} color="white" />
          ) : (
            <ArrowUpCircle color="white" size={16} />
          )}
        </button>
      </div>

      <div className="ai-options-container">
        {AI_OPTIONS.map(opt => (
          <OptionButton
            key={opt.key}
            label={opt.label}
            Icon={opt.Icon}
            onClick={() => onOptionSelect(opt.key, selectedText)}
            disabled={isLoading}
          />
        ))}
      </div>
    </div>
  );

  // Render the menu as a portal to document.body for better positioning
  return createPortal(menuContent, document.body);
};

export default TextMenu;
